/**
 * Data Mode Provider
 *
 * Manages live vs demo mode for the dashboard.
 * Auto-detects when API is unreachable and switches to demo mode.
 * Auto-switches to live mode when user authenticates and API is reachable.
 *
 * ─── Provider Ordering Dependency ───────────────────────────────────────────
 * This provider MUST be a child of AuthProvider in the component tree
 * (see App.tsx). It calls useAuth() to read authentication state.
 * If the provider order is reversed, useAuth() returns the default context
 * (always unauthenticated) and auto-switching silently breaks.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthProvider";
import { API_BASE_URL } from "../services/api";

type DataMode = "live" | "demo" | "public";

/** Visual badge state — derived from mode + auth + API reachability.
 *  "public" = unauthenticated visitor seeing real (truncated) data. */
type BadgeState = "live" | "offline" | "demo" | "public";

interface DataModeContextValue {
  mode: DataMode;
  setMode: (mode: DataMode) => void;
  toggle: () => void;
  apiReachable: boolean;
  /** Canonical badge state for Sidebar/MobileTopBar — always follows mode. */
  badgeState: BadgeState;
}

const DataModeContext = createContext<DataModeContextValue | null>(null);

// ─── Demo-Only Mode (backend decommissioned) ──────────────────────────────
// The Crucio backend (including the /health probe) has been fully torn down.
// The site is now a STATIC DEMO. Mode is hard-pinned to "demo": the health
// check never fires (no request against a dead API), auth is always
// unauthenticated (see AuthProvider), and every data hook already short-
// circuits to demo generators when mode === "demo".
//
// Typed as `boolean` (not the literal `true`) so the live health-check branch
// below stays reachable for the type-checker — the original wiring is kept,
// just gated off. Flip to `false` to restore live/API mode if a backend returns.
const DEMO_ONLY: boolean = true;

/**
 * Check if the API is reachable via root health endpoint.
 *
 * Uses fetch() directly instead of the api client because the health
 * endpoint lives at root /health, not under /api/v1. The api client
 * prepends /api/v1 to all paths, which would hit /api/v1/health (404).
 */
async function checkApiHealth(): Promise<boolean> {
  try {
    const resp = await fetch(`${API_BASE_URL}/health`, {
      signal: AbortSignal.timeout(3000),
    });
    return resp.ok;
  } catch {
    return false;
  }
}

export function DataModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<DataMode>("demo");
  const [apiReachable, setApiReachable] = useState(false);
  const { isAuthenticated } = useAuth();

  // Ref to expose the health check function so the auth effect can
  // trigger an immediate check without waiting for the 30s interval.
  const checkHealthRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // Check API health on mount and periodically
  useEffect(() => {
    // Demo-only: skip the health probe entirely so no request fires against a
    // dead API. Mode stays pinned to "demo" and apiReachable stays false.
    if (DEMO_ONLY) {
      setApiReachable(false);
      setMode("demo");
      return;
    }

    let mounted = true;

    const check = async () => {
      const reachable = await checkApiHealth();
      if (!mounted) return;
      setApiReachable(reachable);
      // Auto-switch to demo if API is unreachable
      if (!reachable) {
        setMode("demo");
      }
    };

    checkHealthRef.current = check;
    check();
    const interval = setInterval(check, 30000); // Re-check every 30s

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  // Track whether the user manually toggled mode. When true, the auth
  // effect won't override their choice on API reconnect.
  const userToggledRef = useRef(false);

  // Auto-switch mode based on auth + API state. The apiReachable dep is
  // needed so we switch to live once the initial health check completes
  // after login. The userToggledRef guard prevents API reconnects from
  // overriding a manual toggle (live→demo).
  useEffect(() => {
    if (isAuthenticated) {
      if (apiReachable && !userToggledRef.current) {
        setMode("live");
      } else if (!apiReachable) {
        checkHealthRef.current?.();
      }
    } else {
      // Unauthenticated visitors: if the API is reachable, show real
      // (truncated) data via "public" mode. If API is down, fall back
      // to mock data. This proves the system is live for portfolio visitors.
      setMode(apiReachable ? "public" : "demo");
      userToggledRef.current = false;
    }
  }, [isAuthenticated, apiReachable]);

  // Guard toggle: unauthenticated users cannot switch to live mode.
  // Only live→demo sets userToggledRef (blocks auto-restore on reconnect).
  // demo→live clears it (allows auto-restore to resume).
  const toggle = useCallback(() => {
    if (!isAuthenticated) return;
    setMode((prev) => {
      if (prev === "live") {
        userToggledRef.current = true;
        return "demo";
      } else {
        userToggledRef.current = false;
        return "live";
      }
    });
  }, [isAuthenticated]);

  // Centralized badge state — Sidebar and MobileTopBar read this instead
  // of re-deriving from auth+apiReachable, ensuring the badge always
  // matches the canonical mode (even if toggle() overrides it).
  // "Offline" only shows when authenticated AND API is actually unreachable,
  // not when the user manually toggled to demo mode.
  const badgeState: BadgeState =
    mode === "live"
      ? "live"
      : mode === "public"
        ? "public"
        : isAuthenticated && !apiReachable
          ? "offline"
          : "demo";

  return (
    <DataModeContext.Provider
      value={{ mode, setMode, toggle, apiReachable, badgeState }}
    >
      {children}
    </DataModeContext.Provider>
  );
}

export function useDataMode() {
  const context = useContext(DataModeContext);
  if (!context) {
    throw new Error("useDataMode must be used within DataModeProvider");
  }
  return context;
}
